import React from "react";
import { useQuery } from "react-query";
import { LoadingDots } from "web-ui";

import { getChangelog } from "api/dashboardBackend";
import type { ChangelogData, ChangelogEntry } from "api/dashboardBackend";
import DuoErrorScreen, { DuoErrorType } from "components/DuoErrorScreen";
import SortableTable, { type Column } from "components/SortableTable";
import styles from "components/changelog/ChangelogContent.module.scss";
import useErrorScreen from "components/useErrorScreen";
import type { PlatformId } from "util/platform";
import { compareSemVers, semVerString } from "util/version";

const getVersionWithDownloadLink = (entry: ChangelogEntry) => {
  if (entry.appVersion === undefined) {
    return "N/A";
  }
  if (entry.appBuildUrl === undefined) {
    return semVerString(entry.appVersion);
  }
  return (
    <a href={entry.appBuildUrl} rel="noreferrer" target="_blank">
      {semVerString(entry.appVersion)}
    </a>
  );
};

/**
 * Converts a class name as specified by the input changelog
 * data to a class name from the stylesheet.
 */
const mapChangelogStyle = (className: string) =>
  ({
    approved: styles.approved,
    bug: styles.bug,
    patch: styles.patch,
    tag: styles.tag,
  })[className];

interface Props {
  platformId: PlatformId;
  fromVersion: string;
  toVersion: string;
}

const prNumberColumn: Column<ChangelogEntry> = {
  getCell: d =>
    d.prReference ? (
      <a href={d.prReference.url} rel="noreferrer" target="_blank">
        {d.prReference.number}
      </a>
    ) : (
      "N/A"
    ),
  getValue: d => d.prReference?.number ?? 0,
  header: "PR",
};

const appVersionColumn: Column<ChangelogEntry> = {
  compare: (a, b) => {
    if (!a.appVersion || !b.appVersion) {
      // Versions sort before "N/A".
      return Number(!a.appVersion) - Number(!b.appVersion);
    }
    return compareSemVers(a.appVersion, b.appVersion);
  },
  getCell: d => getVersionWithDownloadLink(d),
  getValue: d => (d.appVersion ? semVerString(d.appVersion) : "N/A"),
  header: "Version",
};

const titleColumn: Column<ChangelogEntry> = {
  getCell: d =>
    d.commitUrl ? (
      <a href={d.commitUrl} rel="noreferrer" target="_blank">
        <span className={styles.prTitleText}>{d.title}</span>
      </a>
    ) : (
      <span className={styles.prTitleText}>{d.title}</span>
    ),
  getValue: d => d.title,
  header: "Title",
};

const timestampColumn: Column<ChangelogEntry> = {
  getCell: d =>
    d.timestamp
      ? new Date(d.timestamp).toLocaleString("en-US", {
          dateStyle: "short",
          hour12: false,
          timeStyle: "short",
        })
      : "N/A",
  getValue: d => (d.timestamp ? new Date(d.timestamp).valueOf() : 0),
  header: "Time (local)",
};

const authorColumn: Column<ChangelogEntry> = {
  getCell: d => d.author ?? "Unknown",
  getValue: d => d.author ?? "Unknown",
  header: "Author",
};

const teamColumn: Column<ChangelogEntry> = {
  getCell: d => d.teams,
  getValue: d => d.teams.join(", "),
  header: "Team",
};

const jiraColumn: Column<ChangelogEntry> = {
  getCell: d =>
    d.jiraTickets.map(ticket => (
      <a href={ticket.url} key={ticket.key} rel="noreferrer" target="_blank">
        {ticket.key}
      </a>
    )),
  getValue: d => d.jiraTickets.map(ticket => ticket.key).join(", "),
  header: "Jira",
};

const tagColumn: Column<ChangelogEntry> = {
  getCell: d =>
    d.tags.map(tag => (
      <span
        className={tag.extraClassNames
          .map(mapChangelogStyle)
          .filter((s): s is string => !!s)
          .join(" ")}
        key={tag.text}
      >
        {tag.text}
      </span>
    )),
  getValue: d => d.tags.map(tag => tag.text).join(", "),
  header: "Tag",
};

const experimentColumn: Column<ChangelogEntry> = {
  getCell: d =>
    d.experiments.map(exp => (
      <a href={exp.url} key={exp.name} rel="noreferrer" target="_blank">
        {exp.name}
      </a>
    )),
  getValue: d => d.experiments.map(exp => exp.name).join(", "),
  header: "Experiments",
};

const ChangelogContent = ({ platformId, fromVersion, toVersion }: Props) => {
  const { isError, error, isLoading, data } = useQuery<ChangelogData>({
    queryFn: () => getChangelog(platformId, fromVersion, toVersion),
    queryKey: [platformId, fromVersion, toVersion],
  });
  const errorComponent = useErrorScreen(isError, error);

  const [hiddenColumns, setHiddenColumns] = React.useState<Set<string>>(
    new Set(),
  );

  const toggleColumn = (columnHeader: string) => {
    setHiddenColumns(prev => {
      const next = new Set(prev);
      if (next.has(columnHeader)) {
        next.delete(columnHeader);
      } else {
        next.add(columnHeader);
      }
      return next;
    });
  };

  if (errorComponent) {
    return errorComponent;
  }

  const baseColumns = [
    prNumberColumn,
    appVersionColumn,
    titleColumn,
    timestampColumn,
    authorColumn,
    teamColumn,
    jiraColumn,
    tagColumn,
    experimentColumn,
  ];

  const columns = baseColumns.map(col => ({
    ...col,
    isHidden: hiddenColumns.has(String(col.header)),
  }));

  return data && data.entries.length > 0 ? (
    <div className={styles.changelog}>
      <div className={styles.checkboxContainer}>
        {baseColumns.map(col => (
          <label className={styles.columnToggle} key={String(col.header)}>
            <input
              checked={!hiddenColumns.has(String(col.header))}
              onChange={() => toggleColumn(String(col.header))}
              type="checkbox"
            />
            {col.header}
          </label>
        ))}
      </div>
      <div className={styles.changelogContent}>
        <SortableTable columns={columns} data={data.entries} />
      </div>
    </div>
  ) : isLoading ? (
    <LoadingDots type="screen-white" />
  ) : (
    <DuoErrorScreen
      error={Error("Fail to load changelog")}
      errorType={DuoErrorType.UnknownError}
    />
  );
};

export default ChangelogContent;
